import React, { useState } from 'react';
import './UserAvatar.css';
import { useTranslation } from 'react-i18next';

interface UserAvatarProps {
  pictureUrl?: string;
  displayName?: string;
  email?: string;
  phone?: string;
  role?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  clickable?: boolean;
  onShowProfile?: () => void;
}

/**
 * User avatar component that shows a picture or initials badge
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  pictureUrl,
  displayName = 'User',
  email,
  phone,
  role,
  size = 'md',
  clickable = false,
  onShowProfile,
}) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);

  // Generate initials from display name
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  const initials = getInitials(displayName);
  const hasImage = pictureUrl && !imageError;

  const handleClick = () => {
    if (clickable && onShowProfile) {
      onShowProfile();
    }
  };

  const avatarClass = `user-avatar user-avatar--${size} ${
    clickable ? 'user-avatar--clickable' : ''
  }`;

  const content = (
    <>
      {hasImage ? (
        <img
          src={pictureUrl}
          alt={displayName}
          className="user-avatar__image"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="user-avatar__initials">{initials}</div>
      )}
      {clickable && <div className="user-avatar__hover-hint">{t('components.userAvatar.viewProfile')}</div>}
    </>
  );

  // Show profile popover on click
  const element = (
    <div className={avatarClass} onClick={handleClick} role={clickable ? 'button' : undefined}>
      {content}
    </div>
  );

  if (!clickable) {
    return element;
  }

  return (
    <div className="user-avatar-wrapper">
      {element}
      {/* Profile popover content (shown via CSS or separate component) */}
      {displayName && (
        <div className="user-avatar-popover" style={{ display: 'none' }}>
          <div className="user-avatar-popover__content">
            {displayName && <div className="user-avatar-popover__name">{displayName}</div>}
            {role && <div className="user-avatar-popover__role">{role}</div>}
            {email && <div className="user-avatar-popover__email">{email}</div>}
            {phone && <div className="user-avatar-popover__phone">{phone}</div>}
          </div>
        </div>
      )}
    </div>
  );
};
